import { useState, useRef, useEffect } from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle } from "lucide-react";
import { Link } from 'react-router-dom';

interface TermsAndConditionsProps {
  agreedToTerms: boolean;
  onAgreedToTermsChange: (checked: boolean) => void;
  hasAttemptedSubmit?: boolean;
  showValidationError?: boolean;
}

export const TermsAndConditions = ({ 
  agreedToTerms, 
  onAgreedToTermsChange, 
  hasAttemptedSubmit = false,
  showValidationError = false 
}: TermsAndConditionsProps) => {
  const [showTermsWarning, setShowTermsWarning] = useState(false);
  const termsRef = useRef<HTMLDivElement>(null);

  const handleChange = (checked: boolean) => {
    onAgreedToTermsChange(checked);
    if (checked) {
      setShowTermsWarning(false);
    } else {
      setShowTermsWarning(true);
    }
  };

  // Auto-focus and scroll when validation error is shown
  useEffect(() => {
    if (showValidationError && termsRef.current) {
      termsRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Focus the checkbox for accessibility
      const checkbox = termsRef.current.querySelector('button');
      if (checkbox) {
        setTimeout(() => checkbox.focus(), 300);
      }
    }
  }, [showValidationError]);

  const showError = showTermsWarning || (hasAttemptedSubmit && !agreedToTerms) || showValidationError;
  const shouldPulse = hasAttemptedSubmit && !agreedToTerms;

  return (
    <div ref={termsRef}>
      <div className={`
        flex items-start mt-4 p-3 rounded-lg transition-all duration-300
        ${showError ? 
          'bg-destructive/10 border-2 border-destructive/30 shadow-glow' : 
          'bg-muted/30 border border-muted-foreground/20'
        }
        ${shouldPulse ? 'animate-glowPulse' : ''}
      `}>
        <div className="flex items-center h-5">
          <Checkbox 
            id="terms"
            checked={agreedToTerms}
            onCheckedChange={(checked) => handleChange(checked === true)}
            className={`h-4 w-4 transition-all duration-200 ${
              showError ? 
                'border-destructive animate-pulse ring-2 ring-destructive/20' : 
                'border-muted-foreground/40'
            }`}
          />
        </div>
        <div className="ml-3 text-sm">
          <label 
            htmlFor="terms" 
            className={`transition-colors duration-200 cursor-pointer ${
              showError ? 
                'text-destructive font-medium' : 
                'text-muted-foreground'
            }`}
          >
            I agree to the{' '}
            <Link 
              to="/terms" 
              className="text-primary hover:text-primary-light underline decoration-primary/30 hover:decoration-primary transition-colors" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </Link>
            {' '}and{' '}
            <Link 
              to="/privacy" 
              className="text-primary hover:text-primary-light underline decoration-primary/30 hover:decoration-primary transition-colors" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
            <span className={`ml-1 text-xs ${showError ? 'text-destructive' : 'text-muted-foreground/60'}`}>
              *
            </span>
          </label>
        </div>
      </div>
      
      {showError && (
        <div className="flex items-center text-destructive text-sm mt-2 animate-fadeIn">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>Please accept the terms and privacy policy to continue</span>
        </div>
      )}
    </div>
  );
};